import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { SectionsService } from '../../../../services/sections.service';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../../shared/constants/static-data.constants';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../shared/services/logger.service';
import { LeadDetailCommunicationUpdadeModel } from '../../../../interfaces/lead-detail.model';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { AuthorizeFieldDirective } from '../../../../shared/authorization/authorizeField.directive';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { UserService } from '../../../../services/user.service';
import { AuthorizationService } from '../../../../shared/authorization/authorization.service';
import { PageConfig } from '../../../../shared/authorization/auth.config';
import { UserDetails } from '../../../../interfaces/user.interface';
import { DatePickerComponent } from '../../../../shared/components/date-picker/date-picker.component';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';

@Component({
  selector: 'ppl-lead-detail-viewer',
  standalone: true,
  imports: [
    NgZorroModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ListTableComponent,
    AuthorizeDirective,
    AuthorizeFieldDirective,
    ButtonComponent,
    NzDatePickerModule,
    DatePickerComponent,
    DropdownSelectComponent,
  ],
  templateUrl: './lead-detail-viewer.component.html',
  styleUrl: './lead-detail-viewer.component.css',
})
export class LeadDetailViewerComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  mode = 'read';
  filledInfo!: any;
  viewerForm!: FormGroup;
  cols: any;
  gridData: any[] = [];
  communicationType: any;
  copyOfGridData: any = [];
  steps: any;
  entry = 'base';
  target!: LeadDetailCommunicationUpdadeModel;
  associationRole = 0;
  actionButtons: TableRowActionsModel = new TableRowActions();
  approvedStatus: string = STATIC_DATA.APPROVED;
  leadStatus = '';
  leadId = '';
  program: any;
  fiEntries: any;
  mcoEntries: any;
  users: any;
  viewerType = '';
  selectedViewer: any = {};
  isSectionEditable: boolean = false;
  disableAddCaseManager: boolean = false;
  disableAddStateStaff: boolean = false;
  isMobileView = window.innerWidth <= 768;
  viewerData: any;
  staffType = 'MCO';
  programName: any;
  columnsToBeHidden: string[] = [];
  actionsToBeHidden: string[] = [];
  isPageRestrictionLoaded = false;
  actions: any = {'edit': false, 'delete': false}
  isPPLAdmin = false;
  minStartDate?: Date;
  maxEndDate?: Date;
  isAdmin = false;
  isEnrollmentSpecialist = false;

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() readOnly = false;
  @Input() set sectionData(data: string) {
    this.steps = data;
  }

  @Output() leadUpdated = new EventEmitter<boolean>();
  @ViewChild('deleteConfirmationDialog') deleteConfirmationDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    private authUtility: AuthorizationUtility,
    private userService: UserService,
    private authService: AuthorizationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.entry = this.userService.getLeadEntry() ?? '';
    this.leadId = this.userService.getSelectedLead()!;
    const userDetails = this.userService.getUserDetails();
    if (!userDetails) {
      console.error('no user details found');
      return;
    }
    const { usertype_lkp, belongsto_org } = userDetails;
    if (usertype_lkp && belongsto_org && usertype_lkp == 'FI') {
      this.disableAddCaseManager = true;
      this.disableAddStateStaff = true;
    }
    this.isSectionEditable = this.readOnly ? false : this.authUtility.isSectionEditable(this.pageId, this.tabId, 'Viewer');
    this.program = this.userService.getSelectedProgram();
    this.programName = this.program?.program_code ?? '';
    this.communicationType =
      this.lookupService.getLookupValue('Communication_Type');

    this.associationRole = JSON.parse(
      this.userService.getSelectedLead()!
    );
    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;
      
      if (this.leadStatus === this.approvedStatus) {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
      } else {
        this.actionButtons.edit = true;
        this.actionButtons.delete = true;
      }
    });
    this.createFormGroup();
    this.getFromData();
    this.isPPLAdmin = this.userService.isSelectedRole(['PPL Admin', 'BAVAdmin', 'Compliance']) && usertype_lkp === 'FMS';
    this.isEnrollmentSpecialist = this.userService.isSelectedRole('Enrollment Specialist');
    this.lookupService.getOrgData(this.program.program_code,).subscribe((data: any) => {
      if (data.status == 200) {
        if (usertype_lkp && belongsto_org && usertype_lkp == 'FI') {
          this.fiEntries = data.responsedata.filter((item: any) => item.orgTypeLookup === 'FI' && item.orgId == belongsto_org);
        } else {
          this.fiEntries = data.responsedata.filter((item: any) => item.orgTypeLookup === 'FI');
        }
        this.mcoEntries = data.responsedata.filter((item: any) => item.orgTypeLookup === 'MCO');
      }
    })

    this.getViewerData();
    this.viewerForm.get('associationEndDate')?.valueChanges.subscribe(() => {
      this.updateMinStartDate();
    });
    this.viewerForm.get('associationStartDate')?.valueChanges.subscribe(() => {
      this.updateMaxEndDate();
    });
  }

  private setPageAccess() {
      const pageConfig = this.authUtility.getPageConfig(this.pageId);
      if (!pageConfig || !pageConfig.pageName) {
        this.authService.fetchPageAuthorization(this.programName, this.pageId, "LIST").subscribe(response => {
          this.setRestrictions(response);
        })
      } else {
        this.setRestrictions(pageConfig);
      }
    }
  
    private setRestrictions(pageConfig: PageConfig) {
      if (pageConfig) {
        this.columnsToBeHidden = this.authUtility.getHiddenColumns(pageConfig, this.pageId);
        this.actionsToBeHidden = this.authUtility.getHiddenActions(pageConfig, this.pageId);
        const hasEditAccess = !this.authUtility.isFieldEditable(this.pageId, this.tabId, 'Viewer', 'PPL Admin');
        if(hasEditAccess) {
          this.actions.edit = true;
          this.actions.delete = true;
        }
      }
      this.isPageRestrictionLoaded = true;
    }

  getViewerData() {
    this.leadDetailsService.getViewerData(this.program?.program_code, this.leadId).subscribe((data: any) => {
      this.gridData = data?.responsedata?.data;
      this.copyAndTransformGridData(this.gridData);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['participant_viewer'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  sortData(data: any){
    return data.sort((a: any, b: any) => {
      if (a.associationType === 'MCO' && b.associationType !== 'MCO') return -1;
      if (a.associationType !== 'MCO' && b.associationType === 'MCO') return 1;
    
      const firstDate = new Date(a.associationStartDate).getTime();
      const secondDate = new Date(b.associationStartDate).getTime();
      return secondDate - firstDate;
    });
  }

  updateMinStartDate(): void {
    const endDate = this.viewerForm.get('associationEndDate')?.value;
    if (endDate instanceof Date) {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() - 1);
      this.minStartDate = nextDay;
    } else {
      this.minStartDate = undefined;
    }
  }

  updateMaxEndDate(): void {
    const startDate = this.viewerForm.get('associationStartDate')?.value;
    if (startDate instanceof Date) {
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.maxEndDate = nextDay;
    } else {
      this.maxEndDate = undefined;
    }
  }

  createFormGroup() {
    this.viewerForm = this.fb.group({
      id: [],
      participantId: [],
      associationType: [],
      mcoOrgName: [],
      fiOrgName: [],
      associationStartDate: [],
      associationEndDate: [],
      viewerType: [],
      viewerValue: [],
      userId: []
    });
  }

  async getFromData() {
    //table column information
    try {
      if (this.entry) {
        const tempCols = await this.sectionService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_VIEWER_HEADERS,
          this.entry
        );

        this.cols = tempCols?.enrollmentDates?.columns;
        if (!(this.isPPLAdmin || this.isEnrollmentSpecialist)) {
          this.cols = this.cols?.filter((col: any) => col.field !== 'lastModifiedBy');
        }
      }
    } catch (e) {
      this.logger.debug('Error in getting table headers', e);
    }
  }

  viewerValueChange(value: string) {
    // const viewerValue = [value][0];
    if(value){
      this.leadDetailsService
      .getViewerUsers(this.program?.program_code, value, this.viewerType)
      .subscribe((data) => {
        const curerntUsers = data.responsedata;
        curerntUsers.forEach((user: any) => {
          user.identifier = `${user.firstName} ${user.lastName}`
        });

        this.users = curerntUsers;
      });
    }
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.viewerForm.reset();
    this.viewerType = '';
    this.selectedViewer = {};
    setTimeout(() => this.cdr.detectChanges(), 1000);
  }

  action(event: any) {
    console.log('acto', event);
    if (event && event.action === 'edit') {
      this.mode = 'edit';
      if (event?.data) {
        const data = JSON.parse(JSON.stringify(event?.data));
        data.associationStartDate = new Date(data.associationStartDate + 'T00:00:00');
        if (data.associationEndDate) {
          data.associationEndDate = new Date(data.associationEndDate + 'T00:00:00');
        }
        this.viewerForm.patchValue(data);
        this.selectedViewer = data;
      }
    } else if (event && event.action === 'delete') {
      this.delete(event.data);
    }
  }

  copyAndTransformGridData(gridData: any[]) {
    if (gridData) {
      const copyGridData = JSON.parse(JSON.stringify(gridData));
      this.viewerData = copyGridData;
      copyGridData.forEach((data: any) => {
        if(!this.isPPLAdmin){
          data.actions = {
            edit :false,
            delete: false
        }
      }
        else {
          data.actions = {
            edit :true,
            delete: true
        }
        }
        if (data.associationType == 'FI') {
          data.displayAssociationType = 'CDPAP Facilitator';
        } else if(data.associationType == 'FI Staff') {
          data.displayAssociationType = 'CDPAP Facilitator Staff';
        } else {
          data.displayAssociationType = data.associationType;
        }
      })
      this.copyOfGridData = this.sortData(copyGridData);
    }
  }

  disabledStartDate = (startValue: Date): boolean => {
    const endDate = this.viewerForm.get('associationEndDate')?.value;
    if (!startValue || !endDate) {
      return false;
    }
    return startValue.getTime() > endDate.getTime();
  };

  disabledEndDate = (endValue: Date): boolean => {
    const startDate = this.viewerForm.get('associationStartDate')?.value;
    if (!endValue || !startDate) {
      return false;
    }
    return endValue.getTime() <= startDate.getTime();
  };

  add(viewerType: string, associationType: string) {
    this.mode = 'add';
    this.viewerForm.reset();
    this.viewerType = viewerType;
    this.viewerForm.controls['participantId'].setValue(this.associationRole);
    this.viewerForm.controls['associationType'].setValue(associationType);
    this.viewerForm.controls['viewerType'].setValue(viewerType);

    if (viewerType == 'State') {
      this.viewerValueChange('NY');
      this.secData.attributes.filter((e: any) => e.attribute_ID == 'userId')[0].mandatory = 'Y';
      this.secData.validations[0].attributes.push({
        "validatorid": 1,
        "attribute_ID": [
          "userId"
        ],
        "functionParams": {
          "ErrMsg": "Please select user"
        }
      });
      this.secData.validations[0].attributes = this.secData.validations[0].attributes.filter((e: any) => e.attribute_ID.indexOf("mcoOrgName") == -1)
      this.secData.validations[0].attributes = this.secData.validations[0].attributes.filter((e: any) => e.attribute_ID.indexOf("fiOrgName") == -1)
    }
    else if (viewerType == 'MCO') {
      this.secData.attributes.filter((e: any) => e.attribute_ID == 'userId')[0].mandatory = 'N';
      this.secData.validations[0].attributes = this.secData.validations[0].attributes.filter((e: any) => e.attribute_ID.indexOf("userId") == -1)
      this.secData.validations[0].attributes = this.secData.validations[0].attributes.filter((e: any) => e.attribute_ID.indexOf("fiOrgName") == -1)
      this.secData.validations[0].attributes.push({
        "validatorid": 1,
        "attribute_ID": [
          "mcoOrgName"
        ],
        "functionParams": {
          "ErrMsg": "Please select MCO"
        }
      });
    }
    else {
      this.secData.attributes.filter((e: any) => e.attribute_ID == 'userId')[0].mandatory = 'N';
      this.secData.validations[0].attributes = this.secData.validations[0].attributes.filter((e: any) => e.attribute_ID.indexOf("userId") == -1)
      this.secData.validations[0].attributes = this.secData.validations[0].attributes.filter((e: any) => e.attribute_ID.indexOf("mcoOrgName") == -1)
      this.secData.validations[0].attributes.push({
        "validatorid": 1,
        "attribute_ID": [
          "fiOrgName"
        ],
        "functionParams": {
          "ErrMsg": "Please select CDPAPFacilitator"
        }
      });
    }
  }

  save() {
    if (this.mode == 'add') {
      const data = { ...this.viewerForm.value };

      data.associationStartDate = this.dateFormatter.formatToString(data.associationStartDate);

      if (data.associationEndDate) {
        data.associationEndDate = this.dateFormatter.formatToString(data.associationEndDate);
      }

      if (data.viewerType == 'State') {
        data.associationType = 'State Staff';
        data.viewerValue = 'NY';
      } else if (data.viewerType == 'FI') {
        data.viewerValue = data.fiOrgName;
      } else {
        data.viewerValue = data.mcoOrgName;
      }

      try {
        this.validationErrors = performValidation(data, this.secData, 'Save', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType());
      } catch (e) {
        this.logger.debug('Error in validations', e);
      }

      if (Object.keys(this.validationErrors).length === 0) {
        if (data.viewerType === 'MCO') {
          const viewer = this.viewerData.filter(
            (data: any) => data.viewerType === 'MCO'
          );
          this.handleViewerSave(viewer, data);
        } else if (data.viewerType === 'FI') {
          const viewer = this.viewerData.filter(
            (data: any) => data.viewerType === 'FI'
          );
          this.handleViewerSave(viewer, data);
        } else {
          this.saveViewer(data);
        }
      }
    } else if (this.mode == 'edit') {
      this.updateViewer();
    }
  }

  handleViewerSave(viewer: any, data: any) {
    if (viewer && viewer.length > 0) {
      const hasMismatch = viewer.some((item: any) => {
        return item.viewerValue !== data.viewerValue && (!item.associationEndDate || new Date(item.associationEndDate) > new Date())
      }
      );
      if (hasMismatch) {
        this.staffType = data.viewerType;
        this.openConfirmationPopup(data);
      } else {
        this.saveViewer(data);
      }
    } else {
      this.saveViewer(data);
    }
  }  

  openConfirmationPopup(data: any) {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      isOkText: 'Ok',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
    };

    this.dialogSvc.openDialog(
      this.deleteConfirmationDialog,
      dialogConfig,
      () => this.saveViewerOnAdd(data),
      () => this.handleCancel()
    );
  }

  handleCancel() {}

  createViewer(data: any, isAdd = false) {
    this.leadDetailsService
      .createViewer(data, this.program?.program_code, this.leadId, true, data.associationType)
      .subscribe(() => {
        this.logger.info('Viewer saved successfully');
        this.handleCreate(data, isAdd);
      });
  }

  saveViewer(data: any) {
    this.createViewer(data, false);
  }

  saveViewerOnAdd(data: any) {
    this.createViewer(data, true);
  }

  handleCreate(data: any, isAdd = false) {
    if (isAdd) {
      const viewer = this.viewerData.filter((viewerData: any) => {
        if (data.viewerType === 'MCO') {
          return viewerData.associationType === 'MCO' || viewerData.associationType === 'Case Manager';
        } else if (data.viewerType === 'FI') {
          return viewerData.associationType.includes('FI');
        }
        return false;
      });
      viewer.forEach((item: any) => {
        this.updateViewerOnAdd(item.id, item.associationStartDate, item.associationEndDate, item.associationType, data.associationStartDate);
      });
    }
    this.cancelEdit();
    this.getViewerData();
  }

  updateViewer() {
    const formValues = this.viewerForm.value;
    const data: any = {};
    data.id = formValues.id;
    data.associationStartDate = this.dateFormatter.formatToString(formValues.associationStartDate);
    if (formValues.associationEndDate) {
      data.associationEndDate = this.dateFormatter.formatToString(formValues.associationEndDate);
    }
    this.handleUpdate(data,formValues.id, formValues.associationType );
  }

  updateViewerOnAdd(id: any, startDate: any, endDate: any, type: any, assocStartDate: any) {
    const data: any = {};
    data.id = id;
    data.associationStartDate = startDate;
    if(!endDate){
      const localDate = new Date(assocStartDate + 'T00:00:00');
      localDate.setDate(localDate.getDate() - 1);
      data.associationEndDate = this.dateFormatter.formatToString(localDate);
      this.handleUpdate(data, id, type);
    }
  }

  handleUpdate(data: any, id: any, type: string){
    this.leadDetailsService
      .updateViewer(data, this.program?.program_code, id, type)
      .subscribe(() => {
        this.logger.info('Viewer updated successfully');
        this.cancelEdit();
        this.getViewerData();
      });
  }

  delete(record: any) {
    const data: any = {};
    data.id = record.id;
    data.participantId = this.leadId;
    this.leadDetailsService
      .updateInActivateViewer(data, this.program?.program_code, record.id, record.associationType)
      .subscribe(() => {
        this.logger.info('Viewer inactivated successfully');
        this.getViewerData();
      });
  }
}
